import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  Slider,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Select,
  Switch,
  MenuItem,
  TextField,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { API, Storage, graphqlOperation } from 'aws-amplify';
import { Text, View } from '@aws-amplify/ui-react';
import './orgList.css';
import { CSSTransition } from 'react-transition-group';
import gql from 'graphql-tag';
import StageRepository from '../../repository/StageRepository';
import SMSTrigger from './components/SMSTrigger';
import featureFlag from 'application/common/featureFlag';
import { Delete, Add } from '@mui/icons-material';
import { getChangedFields } from './utils/getChangedFields';
import ChangesModal from './CRMConfigV2/ChangesModal';
import useOrgStore from '../../store/zustand/useOrgStore';
import BillingConfig from './components/BillingConfig';

const permissionsArray = [
  { key: 'canCreateAreas', label: 'Can Create Areas' },
  { key: 'canCreateCircles', label: 'Can Create Circles' },
  { key: 'canCreateMissions', label: 'Can Create Missions' },
  { key: 'canCreatePeople', label: 'Can Create People' },
  { key: 'canCreateRewards', label: 'Can Create Rewards' },
  { key: 'canSeeOthersAreas', label: "Can See Others' Areas" },
  { key: 'canSeePins', label: 'Can See Pins' },
];

// Define org fields that get saved - single source of truth
// Add or remove fields here to automatically update both save and change detection
const ORG_FIELDS_TO_SAVE = [
  'name',
  'contactName',
  'contactPhone',
  'contactEmail',
  'contactAddress',
  'contactAddress2',
  'contactCity',
  'contactState',
  'contactZip',
  'paymentCustomerID',
  'billingType',
  'planType',
  'salesChannel',
  'selfServiceCancellation',
  'bypassBillingCheck',
  'trials',
];

// Maps a Trial object from the org to a UI row
function mapTrialToRow(trial) {
  const estimatesFeature = trial.features?.find((f) => f.type === 'estimate');
  const mrFeature = trial.features?.find((f) => f.type === 'measurementReport');
  return {
    startDate: trial.startDate ?? '',
    estimatesCount: estimatesFeature?.count ?? 0,
    measurementReportsCount: mrFeature?.count ?? 0,
    length: trial.length ?? 7,
  };
}

// Serializes a UI row back to a Trial object for the mutation
function serializeTrialRow(row) {
  return {
    startDate: row.startDate,
    features: [
      { count: row.estimatesCount, type: 'estimate' },
      { count: row.measurementReportsCount, type: 'measurementReport' },
    ],
    length: row.length ?? 7,
    type: 'plg',
  };
}

export default function GeneralConfig(props) {
  const { org, setOrg, orgID, featuresList, setFeaturesList, onSaveSuccess } =
    props;
  const [defaultScheduleObj, setDefaultScheduleObj] = useState(null);
  const [ogDefaultScheduleObj, setOgDefaultScheduleObj] = useState(null);
  const [ogOrgObj, setOgOrgObj] = useState(null);
  const [ogFeaturesList, setOgFeaturesList] = useState([]);
  // const [org, setOrg] = useState(null);
  const orgStateSetterCalledRef = useRef(false);

  const [success, setSuccess] = useState(false);
  const [orgUSers, setOrgUsers] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);
  const [orgTiles, setOrgTiles] = useState([]);
  const [ogOrgTiles, setOgOrgTiles] = useState([]);
  const [orgPermissions, setOrgPermissions] = useState({});
  const [ogOrgPermissions, setOgOrgPermissions] = useState({});
  const [orgRoles, setOrgRoles] = useState([]);
  const [smsVariables, setSmsVariables] = useState([]);
  const [loadingSmsVariables, setLoadingSmsVariables] = useState(true); // I am using this to ensure that the data is loaded before the sms triggers are rendered
  const [damageReportsEnabled, setDamageReportsEnabled] = useState(false);
  const [damageReportsLogo, setDamageReportsLogo] = useState('');
  const [damageReportsHeroImages, setDamageReportsHeroImages] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [changed, setChanged] = useState(false);
  const saveChangesRef = useRef(null);
  const [seatCount, setSeatCount] = useState(0);
  const [estimateCredits, setEstimateCredits] = useState({
    granted: 0,
    used: 0,
    remaining: 0,
  });
  const [creditsToAdd, setCreditsToAdd] = useState('');
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState('');
  const [creditsSuccess, setCreditsSuccess] = useState('');
  const [irCredits, setIrCredits] = useState({
    granted: 0,
    used: 0,
    remaining: 0,
  });
  const [irCreditsToAdd, setIrCreditsToAdd] = useState('');
  const [irCreditsLoading, setIrCreditsLoading] = useState(false);
  const [irCreditsError, setIrCreditsError] = useState('');
  const [irCreditsSuccess, setIrCreditsSuccess] = useState('');
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false);
  const [showConfirmResetModal, setShowConfirmResetModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [exemptFirstName, setExemptFirstName] = useState('');
  const [exemptLastName, setExemptLastName] = useState('');
  const [exemptEmail, setExemptEmail] = useState('');
  const [exemptPhone, setExemptPhone] = useState('');
  const [exemptError, setExemptError] = useState('');
  const [exemptSuccess, setExemptSuccess] = useState('');
  const [exemptLoading, setExemptLoading] = useState(false);
  const [bypassBillingCheck, setBypassBillingCheck] = useState(true);
  const [trials, setTrials] = useState([]);
  const [ogBypassBillingCheck, setOgBypassBillingCheck] = useState(true);
  const [ogTrials, setOgTrials] = useState([]);
  const [trialsError, setTrialsError] = useState('');
  const [trialsDeserializationError, setTrialsDeserializationError] =
    useState(false);
  const setOrgData = useOrgStore((state) => state.setOrgData);

  const stripePaymentProcessorMutation = /* GraphQL */ `
    mutation StripePaymentProcessor($request: AWSJSON!) {
      stripePaymentProcessor(request: $request)
    }
  `;

  const fetchEstimateCredits = async () => {
    try {
      const request = JSON.stringify({
        operation: 'getOrgEntitlements',
        orgID,
      });
      const result = await API.graphql(
        graphqlOperation(stripePaymentProcessorMutation, { request })
      );
      let parsed = result?.data?.stripePaymentProcessor;
      while (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          break;
        }
      }
      if (parsed?.success && parsed.data) {
        setEstimateCredits(parsed.data);
      }
    } catch (err) {
      console.error('Error fetching estimate credits:', err);
    }
  };

  const handleGrantCredits = async () => {
    const num = parseInt(creditsToAdd, 10);
    if (!num || num < 1) {
      setCreditsError('Enter a positive number of credits');
      return;
    }
    setCreditsLoading(true);
    setCreditsError('');
    setCreditsSuccess('');
    try {
      const request = JSON.stringify({
        operation: 'grantEstimateCredits',
        orgID,
        credits: num,
      });
      const result = await API.graphql(
        graphqlOperation(stripePaymentProcessorMutation, { request })
      );
      let parsed = result?.data?.stripePaymentProcessor;
      while (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          break;
        }
      }
      if (parsed?.success && parsed.data) {
        setEstimateCredits(parsed.data);
        setCreditsToAdd('');
        setCreditsSuccess(
          `Added ${num} credit${num > 1 ? 's' : ''} successfully`
        );
        setTimeout(() => setCreditsSuccess(''), 4000);
      } else {
        setCreditsError(parsed?.error || 'Failed to grant credits');
      }
    } catch (err) {
      console.error('Error granting credits:', err);
      setCreditsError('Failed to grant credits');
    } finally {
      setCreditsLoading(false);
    }
  };

  const fetchIrCredits = async () => {
    try {
      const request = JSON.stringify({
        operation: 'getOrgEntitlements',
        orgID,
        resource_type: 'instant_roofer_report',
      });
      const result = await API.graphql(
        graphqlOperation(stripePaymentProcessorMutation, { request })
      );
      let parsed = result?.data?.stripePaymentProcessor;
      while (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          break;
        }
      }
      if (parsed?.success && parsed.data) {
        const irData = Array.isArray(parsed.data)
          ? parsed.data.find((e) => e.resource_type === 'instant_roofer_report')
          : parsed.data.resource_type === 'instant_roofer_report'
          ? parsed.data
          : null;
        if (irData) {
          setIrCredits({
            granted: irData.granted ?? 0,
            used: irData.used ?? 0,
            remaining: (irData.granted ?? 0) - (irData.used ?? 0),
          });
        }
      }
    } catch (err) {
      console.error('Error fetching IR report credits:', err);
    }
  };

  const handleGrantIrCredits = async () => {
    const num = parseInt(irCreditsToAdd, 10);
    if (!num || num < 1) {
      setIrCreditsError('Enter a positive number of credits');
      return;
    }
    setIrCreditsLoading(true);
    setIrCreditsError('');
    setIrCreditsSuccess('');
    try {
      const request = JSON.stringify({
        operation: 'grantIRReportCredits',
        orgID,
        credits: num,
      });
      const result = await API.graphql(
        graphqlOperation(stripePaymentProcessorMutation, { request })
      );
      let parsed = result?.data?.stripePaymentProcessor;
      while (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          break;
        }
      }
      if (parsed?.success && parsed.data) {
        const irData = Array.isArray(parsed.data)
          ? parsed.data.find((e) => e.resource_type === 'instant_roofer_report')
          : parsed.data.resource_type === 'instant_roofer_report'
          ? parsed.data
          : parsed.data;
        setIrCredits({
          granted: irData.granted ?? 0,
          used: irData.used ?? 0,
          remaining: (irData.granted ?? 0) - (irData.used ?? 0),
        });
        setIrCreditsToAdd('');
        setIrCreditsSuccess(
          `Added ${num} credit${num > 1 ? 's' : ''} successfully`
        );
        setTimeout(() => setIrCreditsSuccess(''), 4000);
      } else {
        setIrCreditsError(parsed?.error || 'Failed to grant credits');
      }
    } catch (err) {
      console.error('Error granting IR report credits:', err);
      setIrCreditsError('Failed to grant credits');
    } finally {
      setIrCreditsLoading(false);
    }
  };

  const fetchSmsVariables = async (orgID) => {
    try {
      setLoadingSmsVariables(true);
      const category = await StageRepository.fetchDefaultStageCategory(orgID);
      if (category && category.flexAttributes?.config?.deal?.flexFields) {
        setSmsVariables(category.flexAttributes.config.deal.flexFields);
      }
      setLoadingSmsVariables(false);
    } catch (error) {
      console.error('Error fetching SMS variables:', error);
      setLoadingSmsVariables(false);
    }
  };

  const uploadLogo = async (file) => {
    try {
      setUploadingLogo(true);
      const fileName = `damage-reports/logo/${orgID}/${Date.now()}_${
        file.name
      }`;
      await Storage.put(fileName, file, {
        contentType: file.type,
      });
      setDamageReportsLogo(fileName);
    } catch (error) {
      console.error('Error uploading logo:', error);
    } finally {
      setUploadingLogo(false);
    }
  };

  const uploadHeroImage = async (file) => {
    try {
      setUploadingHeroImage(true);
      const fileName = `damage-reports/hero-images/${orgID}/${Date.now()}_${
        file.name
      }`;
      await Storage.put(fileName, file, {
        contentType: file.type,
      });
      setDamageReportsHeroImages([...damageReportsHeroImages, fileName]);
    } catch (error) {
      console.error('Error uploading hero image:', error);
    } finally {
      setUploadingHeroImage(false);
    }
  };

  const removeHeroImage = (index) => {
    const newHeroImages = damageReportsHeroImages.filter((_, i) => i !== index);
    setDamageReportsHeroImages(newHeroImages);
  };

  const defaultScheduleTemp = {
    default: [
      {
        days: [0, 1, 2, 3, 4, 5],
        defaultMeetingLength: 90,
        endTime: 1260,
        maximumMeetingLength: 600,
        minimumMeetingLength: 30,
        minutesBetweenMeetings: 0,
        startTime: 540,
      },
    ],
  };

  async function getOrgMembers() {
    if (fetchingData) return;
    setFetchingData(true);
    const myQuery = gql`
      query ListUsersByOrg(
        $orgID: ID
        $email: ModelStringKeyConditionInput
        $sortDirection: ModelSortDirection
        $nextToken: String
      ) {
        listUsersByOrg(
          orgID: $orgID
          email: $email
          sortDirection: $sortDirection
          filter: { status: { ne: "deleted" } }
          limit: 150
          nextToken: $nextToken
        ) {
          items {
            id
            username
            email
            name
            excludeFromBilling
          }
          nextToken
        }
      }
    `;
    try {
      const usersList = [];
      let ourNextToken = null;
      do {
        const res = await API.graphql({
          query: myQuery,
          variables: { orgID, nextToken: ourNextToken },
        });
        usersList.push(...res.data.listUsersByOrg.items);
        ourNextToken = res.data.listUsersByOrg.nextToken;
      } while (ourNextToken);
      usersList.sort((a, b) => a.name.localeCompare(b.name));
      setOrgUsers(usersList);
      setFetchingData(false);
    } catch (e) {
      setFetchingData(false);
      console.error(e);
    }
  }

  async function getOrgRoles(orgID) {
    const myQuery = gql`
      query ListRolesByOrg($orgID: ID!) {
        listRolesByOrg(orgID: $orgID, filter: { isDeleted: { eq: false } }) {
          items {
            id
            roleName
          }
        }
      }
    `;
    API.graphql({
      query: myQuery,
      variables: { orgID },
    }).then((res) => {
      const roles = res.data.listRolesByOrg.items;
      setOrgRoles(roles);
    });
  }

  const orgStatesSetter = (org) => {
    if (!org) return;
    setOgOrgObj(org);
    const { features, defaultSchedule, tiles, permissions, damageReports } =
      JSON.parse(org.configParms);

    // Use the same merging logic as parent component for consistency
    const defaultFeatures = {
      route: {
        enabled: false,
        onlyCreatorCanChangeDisposition: false,
        onlyCreatorCanDelete: false,
        onlyCreatorCanEdit: false,
        onlyCreatorCanViewDetails: false,
        enhancedDoorData: false,
        wanderMode: false,
        disableSearch: false,
        radius: 1000,
        maxPinDistance: 10,
        maxPinDistanceCheck: false,
        mapboxKey: '',
      },
      feed: {
        enabled: false,
        comment: false,
        like: false,
        uploadVideo: false,
      },
      feedBlast: {
        enabled: false,
        adminOnly: false,
        deafultPostToAll: false,
        scheduleSend: false,
        circle: 'user',
        individual: 'user',
      },
      person: {
        enabled: false,
        uploadPicture: false,
        uploadVideo: false,
      },
      circle: {
        enabled: false,
        uploadPicture: false,
        uploadVideo: false,
      },
      reward: {
        enabled: false,
        uploadPicture: false,
        uploadVideo: false,
      },
      deal: {
        enabled: false,
        disableCircleFilter: false,
        disableUserFilter: false,
        signOnDevice: false,
        signUsingEmail: false,
        disableChangeStage: false,
        noOrderLinesRequiredForSigning: false,
        orderTableFields: [],
        closerEqualsSetter: false,
        showDispositionOnDealsList: false,
        trackDealViews: false,
        trackDealViewsDebounceMinutes: 3,
      },
      schedule: {
        enabled: false,
        autoSchedule: false,
        useRoundRobin: false,
        schedulerRole: [],
        appointmentNotifications: {
          enabled: false,
          triggers: [],
        },
      },
      games: {
        enabled: true,
        bracketCreation: true,
      },
      dashboard: {
        disabled: false,
      },
      webInterface: {
        hideProducts: false,
        hideFeed: false,
        disableScheduling: false,
      },
    };

    const mergedFeatures = JSON.parse(JSON.stringify(defaultFeatures)); // Deep copy

    if (features) {
      for (const featureKey in features) {
        if (Object.prototype.hasOwnProperty.call(features, featureKey)) {
          if (
            typeof mergedFeatures[featureKey] === 'object' &&
            mergedFeatures[featureKey] !== null &&
            !Array.isArray(mergedFeatures[featureKey]) &&
            typeof features[featureKey] === 'object'
          ) {
            // It's an object, merge it
            mergedFeatures[featureKey] = {
              ...mergedFeatures[featureKey],
              ...features[featureKey],
            };
          } else {
            // It's a new feature or not an object, just assign
            mergedFeatures[featureKey] = features[featureKey];
          }
        }
      }
    }

    // Don't set featuresList here since it's managed by parent component
    // setFeaturesList(() => features);
    setOgFeaturesList(() => mergedFeatures);
    setDefaultScheduleObj(() => defaultSchedule);
    setOgDefaultScheduleObj(() => defaultSchedule);
    setOrgTiles(tiles);
    setOgOrgTiles(tiles);
    setOrgPermissions(permissions || {});
    setOgOrgPermissions(permissions || {});

    // Set damage reports configuration
    if (damageReports) {
      setDamageReportsEnabled(damageReports.enabled || false);
      setDamageReportsLogo(damageReports.logo || '');
      setDamageReportsHeroImages(damageReports.heroImages || []);
    }

    // Set users seat count configuration
    const parsedConfig = JSON.parse(org.configParms);
    const parsedSeatCount = Number.isFinite(parsedConfig?.seat_count)
      ? parsedConfig.seat_count
      : 0;
    setSeatCount(parsedSeatCount);

    // bypassBillingCheck: default true when absent, null, or blank string
    const rawBypass = org.bypassBillingCheck;
    const parsedBypass =
      rawBypass === null || rawBypass === undefined || rawBypass === ''
        ? true
        : Boolean(rawBypass);
    setBypassBillingCheck(parsedBypass);
    setOgBypassBillingCheck(parsedBypass);

    // trials: parse array, map each entry to a row
    let parsedTrials = [];
    try {
      const rawTrials = org.trials;
      if (Array.isArray(rawTrials)) {
        parsedTrials = rawTrials.map(mapTrialToRow);
      } else if (typeof rawTrials === 'string' && rawTrials) {
        parsedTrials = JSON.parse(rawTrials).map(mapTrialToRow);
      }
    } catch (e) {
      setTrialsDeserializationError(true);
      return;
    }
    setTrials(parsedTrials);
    setOgTrials(parsedTrials);
  };

  const formatTime = (time) => {
    let hour = Math.floor(time / 60);
    let min = Math.floor(time % 60);
    let ampm = 'PM';
    if (hour < 12) {
      ampm = 'AM';
    } else if (hour > 12) {
      hour = hour - 12;
    }
    if (hour < 10) {
      hour = '0' + hour;
    }

    if (min < 10) {
      min = '0' + min;
    }
    return `${hour}:${min} ${ampm}`;
  };

  useEffect(() => {
    // getOrgData(orgID);
    getOrgRoles(orgID);
    getOrgMembers();
    fetchSmsVariables(orgID);
    fetchEstimateCredits();
    fetchIrCredits();
  }, []);

  // Initialize defaultSchedule when org data is loaded (only once)
  useEffect(() => {
    if (org && !orgStateSetterCalledRef.current) {
      orgStateSetterCalledRef.current = true;
      orgStatesSetter(org);
    }
  }, [org, ogOrgObj]);

  // Helper function to get all changes (excluding metadata fields)
  // This compares ONLY the fields that get saved in onSubmit
  const getAllChanges = () => {
    if (!ogOrgObj) return [];

    const allChanges = [];

    // Compare specific org fields that get saved (uses ORG_FIELDS_TO_SAVE constant)
    ORG_FIELDS_TO_SAVE.forEach((field) => {
      const oldValue = ogOrgObj[field];
      const newValue = org[field];
      if (oldValue !== newValue) {
        allChanges.push({
          fieldName: `Organization > ${field
            .replace(/([A-Z])/g, ' $1')
            .trim()}`,
          oldValue: oldValue || '(empty)',
          newValue: newValue || '(empty)',
        });
      }
    });

    // Compare features changes
    const featuresChanges = getChangedFields(
      ogFeaturesList,
      featuresList,
      'Features'
    );
    allChanges.push(...featuresChanges);

    // Compare schedule changes
    const scheduleChanges = getChangedFields(
      ogDefaultScheduleObj,
      defaultScheduleObj,
      'Default Schedule'
    );
    allChanges.push(...scheduleChanges);

    // Compare permissions changes
    const permissionsChanges = getChangedFields(
      ogOrgPermissions,
      orgPermissions,
      'Permissions'
    );
    allChanges.push(...permissionsChanges);

    // Compare tiles changes (deep comparison, not just count)
    const tilesChanges = getChangedFields(ogOrgTiles, orgTiles, 'Tiles');
    allChanges.push(...tilesChanges);

    // Compare seat count
    const ogParsed = JSON.parse(ogOrgObj?.configParms || '{}');
    const originalSeatCount = Number.isFinite(ogParsed?.seat_count)
      ? ogParsed.seat_count
      : 0;
    const currentSeatCount = Number.isFinite(Number(seatCount))
      ? Math.max(0, Math.floor(Number(seatCount)))
      : 0;
    if (originalSeatCount !== currentSeatCount) {
      allChanges.push({
        fieldName: 'Seat Count',
        oldValue: String(originalSeatCount),
        newValue: String(currentSeatCount),
      });
    }

    // Compare bypass billing check
    if (bypassBillingCheck !== ogBypassBillingCheck) {
      allChanges.push({
        fieldName: 'Licensing > Bypass Billing Check',
        oldValue: ogBypassBillingCheck ? 'Enabled' : 'Disabled',
        newValue: bypassBillingCheck ? 'Enabled' : 'Disabled',
      });
    }

    // Compare trials (deep comparison via JSON serialization)
    if (
      JSON.stringify(trials.map(serializeTrialRow)) !==
      JSON.stringify(ogTrials.map(serializeTrialRow))
    ) {
      allChanges.push({
        fieldName: 'Licensing > Trials',
        oldValue: `${ogTrials.length} trial(s)`,
        newValue: `${trials.length} trial(s)`,
      });
    }

    // Compare damage reports
    const originalDamageReports = ogParsed?.damageReports || {};
    if (damageReportsEnabled !== (originalDamageReports.enabled || false)) {
      allChanges.push({
        fieldName: 'Damage Reports > Enabled',
        oldValue: originalDamageReports.enabled ? 'Yes' : 'No',
        newValue: damageReportsEnabled ? 'Yes' : 'No',
      });
    }
    if (damageReportsLogo !== (originalDamageReports.logo || '')) {
      allChanges.push({
        fieldName: 'Damage Reports > Logo',
        oldValue: originalDamageReports.logo || '(empty)',
        newValue: damageReportsLogo || '(empty)',
      });
    }
    // Compare damage reports hero images
    const heroImagesChanges = getChangedFields(
      originalDamageReports.heroImages || [],
      damageReportsHeroImages,
      'Damage Reports > Hero Images'
    );
    allChanges.push(...heroImagesChanges);

    return allChanges;
  };

  useEffect(() => {
    // Only check for changes if all original states are initialized
    if (
      !ogOrgObj ||
      !ogFeaturesList ||
      Object.keys(ogFeaturesList).length === 0
    ) {
      setChanged(false);
      return;
    }

    // Use getAllChanges() to check for actual changes (excluding metadata)
    const actualChanges = getAllChanges();
    setChanged(actualChanges.length > 0);
  }, [
    featuresList,
    org,
    defaultScheduleObj,
    orgTiles,
    orgPermissions,
    ogOrgObj,
    ogFeaturesList,
    ogDefaultScheduleObj,
    ogOrgTiles,
    ogOrgPermissions,
    damageReportsEnabled,
    damageReportsLogo,
    damageReportsHeroImages,
    seatCount,
    bypassBillingCheck,
    ogBypassBillingCheck,
  ]);

  // Separately fetch org members when autoSchedule is enabled — kept out of
  // the change-detection effect to avoid triggering re-renders in a loop
  useEffect(() => {
    if (
      featuresList?.schedule?.enabled &&
      featuresList?.schedule?.autoSchedule &&
      orgUSers === null
    ) {
      getOrgMembers();
    }
  }, [featuresList?.schedule?.enabled, featuresList?.schedule?.autoSchedule]);

  const onSubmit = async () => {
    // Build GraphQL mutation dynamically from ORG_FIELDS_TO_SAVE constant
    const orgFieldsString = ORG_FIELDS_TO_SAVE.join('\n          ');
    const myMutation = gql`
      mutation UpdateOrg($input: UpdateOrgInput!) {
        updateOrg(input: $input) {
          id
          ${orgFieldsString}
          configParms
        }
      }
    `;
    const currentConfigParms = JSON.parse(org.configParms);
    const newConfigParms = {
      ...currentConfigParms,
      features: featuresList,
      permissions: orgPermissions,
      defaultSchedule: defaultScheduleObj,
      tiles: orgTiles,
      damageReports: {
        enabled: damageReportsEnabled,
        logo: damageReportsLogo,
        heroImages: damageReportsHeroImages,
      },
      seat_count: Math.max(0, Math.floor(Number(seatCount)) || 0),
      publish: {
        ...currentConfigParms.publish,
        appointment:
          featuresList.schedule.appointmentNotifications?.enabled || false,
      },
    };
    // Build the mutation input dynamically from ORG_FIELDS_TO_SAVE
    const mutationInput = {
      id: org.id,
      configParms: JSON.stringify(newConfigParms),
    };

    // bypassBillingCheck is local state (not on org), so assign explicitly before the loop
    mutationInput.bypassBillingCheck = bypassBillingCheck;

    // Add all org fields dynamically
    ORG_FIELDS_TO_SAVE.forEach((field) => {
      mutationInput[field] = org[field];
    });

    // Validate trials: each row must have at least one non-zero count
    const hasInvalidTrialRow = trials.some(
      (row) => row.estimatesCount === 0 && row.measurementReportsCount === 0
    );
    if (hasInvalidTrialRow) {
      setTrialsError(
        'Each trial must have at least one non-zero count (Estimates or Measurement Reports).'
      );
      return;
    }

    // Serialize trials and assign to mutation input
    mutationInput.trials = trials.map(serializeTrialRow);

    const res = await API.graphql({
      query: myMutation,
      variables: {
        input: mutationInput,
      },
    });
    const updatedOrg = res.data.updateOrg;

    // Update the parent's org state with the saved data
    setOrg(updatedOrg);

    setOgOrgObj(updatedOrg);
    setOgBypassBillingCheck(bypassBillingCheck);
    setOgTrials([...trials]);
    setOgFeaturesList({ ...featuresList });
    setOgDefaultScheduleObj({ ...defaultScheduleObj });
    setOgOrgTiles([...orgTiles]);
    setOgOrgPermissions({ ...orgPermissions });

    // Update global.appSettings.features so NavBar reflects changes immediately
    if (global.appSettings) {
      global.appSettings.features = { ...featuresList };
    }

    // Update Zustand store so NavBar re-renders with new config
    const parsedUpdatedConfig = JSON.parse(updatedOrg.configParms);
    setOrgData({
      ...updatedOrg,
      configParms: parsedUpdatedConfig,
    });

    // Call parent's save success handler
    if (onSaveSuccess) {
      onSaveSuccess(updatedOrg, featuresList);
    }

    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 3000);
  };

  const resetToOriginal = () => {
    if (ogOrgObj) {
      setOrg(ogOrgObj);
      orgStatesSetter(ogOrgObj);
      setFeaturesList(ogFeaturesList);
      setDefaultScheduleObj(ogDefaultScheduleObj);
      setOrgTiles([...ogOrgTiles]);
      setOrgPermissions({ ...ogOrgPermissions });
    }
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      await onSubmit();
      setShowConfirmSaveModal(false);
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmReset = () => {
    resetToOriginal();
    setShowConfirmResetModal(false);
  };

  if (!org || !featuresList || Object.keys(featuresList).length === 0) {
    return <CircularProgress color="inherit" />;
  }

  if (trialsDeserializationError) {
    return (
      <Alert severity="error">
        Failed to load trials data. The trials field may be malformed. Please
        contact support.
      </Alert>
    );
  }

  return (
    <View style={{ position: 'relative', width: '100%' }}>
      <CSSTransition
        in={changed}
        timeout={300}
        classNames="slide"
        unmountOnExit
        nodeRef={saveChangesRef}
      >
        <Box ref={saveChangesRef} className="save-changes-box">
          <Button
            onClick={() => setShowChangesModal(true)}
            variant="outlined"
            color="secondary"
          >
            View Changes
          </Button>
          <Box style={{ width: '8px' }} />
          <Button
            variant="outlined"
            color="error"
            onClick={() => setShowConfirmResetModal(true)}
          >
            Reset Org
          </Button>
          <Box style={{ width: '8px' }} />
          <Button
            onClick={() => setShowConfirmSaveModal(true)}
            variant="contained"
            color="primary"
          >
            Save Changes
          </Button>
        </Box>
      </CSSTransition>
      <Box
        style={{
          backgroundColor: 'white',
          border: '1px solid black',
          borderRadius: '15px',
        }}
      >
        <Box id="general-info-box">
          <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
            <Text style={{ width: '12vw', fontWeight: 'bold' }}>Name</Text>
            <TextField
              style={{ width: '90%' }}
              variant="standard"
              name="name"
              value={org.name}
              onChange={(e) => setOrg(() => ({ ...org, name: e.target.value }))}
            />
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
            <Text style={{ width: '12vw', fontWeight: 'bold' }}>
              Contact Name
            </Text>
            <TextField
              style={{ width: '90%' }}
              variant="standard"
              name="contactName"
              value={org.contactName}
              onChange={(e) =>
                setOrg(() => ({ ...org, contactName: e.target.value }))
              }
            />
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
            <Text style={{ width: '12vw', fontWeight: 'bold' }}>
              Contact Phone
            </Text>
            <TextField
              style={{ width: '90%' }}
              variant="standard"
              name="contactPhone"
              value={org.contactPhone}
              onChange={(e) =>
                setOrg(() => ({ ...org, contactPhone: e.target.value }))
              }
            />
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
            <Text style={{ width: '12vw', fontWeight: 'bold' }}>
              Contact Email
            </Text>
            <TextField
              style={{ width: '90%' }}
              variant="standard"
              name="contactEmail"
              value={org.contactEmail}
              onChange={(e) =>
                setOrg(() => ({ ...org, contactEmail: e.target.value }))
              }
            />
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
            <Text style={{ width: '12vw', fontWeight: 'bold' }}>Address</Text>
            <TextField
              style={{ width: '90%' }}
              variant="standard"
              name="contactAddress"
              value={org.contactAddress}
              onChange={(e) =>
                setOrg(() => ({ ...org, contactAddress: e.target.value }))
              }
            />
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
            <Text style={{ width: '12vw', fontWeight: 'bold' }}>
              Address Line 2
            </Text>
            <TextField
              style={{ width: '90%' }}
              variant="standard"
              name="contactAddress2"
              value={org.contactAddress2}
              onChange={(e) =>
                setOrg(() => ({ ...org, contactAddress2: e.target.value }))
              }
            />
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
            <Text style={{ width: '12vw', fontWeight: 'bold' }}>City</Text>
            <TextField
              style={{ width: '90%' }}
              variant="standard"
              name="contactCity"
              value={org.contactCity}
              onChange={(e) =>
                setOrg(() => ({ ...org, contactCity: e.target.value }))
              }
            />
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
            <Text style={{ width: '12vw', fontWeight: 'bold' }}>State</Text>
            <TextField
              style={{ width: '90%' }}
              variant="standard"
              name="contactState"
              value={org.contactState}
              onChange={(e) =>
                setOrg(() => ({ ...org, contactState: e.target.value }))
              }
            />
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
            <Text style={{ width: '12vw', fontWeight: 'bold' }}>
              Postal Code
            </Text>
            <TextField
              style={{ width: '90%' }}
              variant="standard"
              name="contactZip"
              value={org.contactZip}
              onChange={(e) =>
                setOrg(() => ({ ...org, contactZip: e.target.value }))
              }
            />
          </Box>
        </Box>
      </Box>
      <Box
        style={{
          backgroundColor: 'white',
          border: '1px solid black',
          borderRadius: '15px',
          padding: '10px',
          marginTop: '10px',
        }}
      >
        <BillingConfig
          org={org}
          onChange={(field, value) =>
            setOrg((prev) => ({ ...prev, [field]: value }))
          }
          onSavedExternally={(fields) => {
            // Update both the form state and the baseline so these
            // backend-persisted fields don't appear as unsaved changes
            setOrg((prev) => ({ ...prev, ...fields }));
            setOgOrgObj((prev) => (prev ? { ...prev, ...fields } : prev));
          }}
          bypassBillingCheck={bypassBillingCheck}
          onBypassBillingCheckChange={() =>
            setBypassBillingCheck((prev) => !prev)
          }
          seatCount={seatCount}
          onSeatCountChange={(val) => setSeatCount(val)}
        />
        {!bypassBillingCheck && (
          <Box
            style={{
              marginTop: '20px',
              borderTop: '1px solid #ddd',
              paddingTop: '15px',
            }}
          >
            <Text
              style={{
                fontWeight: 'bold',
                fontSize: '1.2rem',
                marginBottom: '10px',
              }}
            >
              Billing-Exempt Users
            </Text>

            {(() => {
              const exemptUsers =
                orgUSers?.filter((u) => u.excludeFromBilling === true) || [];
              const nonExemptUsers =
                orgUSers?.filter((u) => u.excludeFromBilling !== true) || [];

              return (
                <>
                  {/* Exempt Users Table */}
                  {exemptUsers.length === 0 ? (
                    <Text
                      style={{
                        color: '#888',
                        fontStyle: 'italic',
                        margin: '10px 0',
                      }}
                    >
                      No billing-exempt users.
                    </Text>
                  ) : (
                    <Box style={{ margin: '10px 0' }}>
                      <Box
                        style={{
                          display: 'flex',
                          fontWeight: 'bold',
                          padding: '5px 0',
                          borderBottom: '1px solid #ccc',
                        }}
                      >
                        <Text style={{ flex: 1 }}>Name</Text>
                        <Text style={{ flex: 1 }}>Email</Text>
                        <Text style={{ width: '180px', textAlign: 'center' }}>
                          Actions
                        </Text>
                      </Box>
                      {exemptUsers.map((user) => (
                        <Box
                          key={user.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '5px 0',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          <Text style={{ flex: 1 }}>{user.name}</Text>
                          <Text style={{ flex: 1 }}>{user.email}</Text>
                          <Box
                            style={{
                              width: '180px',
                              display: 'flex',
                              justifyContent: 'center',
                              gap: '8px',
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={async () => {
                                try {
                                  const updateMutation = gql`
                                    mutation UpdateUser(
                                      $input: UpdateUserInput!
                                    ) {
                                      updateUser(input: $input) {
                                        id
                                        excludeFromBilling
                                      }
                                    }
                                  `;
                                  await API.graphql({
                                    query: updateMutation,
                                    variables: {
                                      input: {
                                        id: user.id,
                                        excludeFromBilling: false,
                                      },
                                    },
                                  });
                                  await getOrgMembers();
                                } catch (err) {
                                  console.error(
                                    'Error removing exemption:',
                                    err
                                  );
                                  setExemptError(
                                    'Failed to remove billing exemption.'
                                  );
                                }
                              }}
                            >
                              Remove
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={async () => {
                                try {
                                  const updateMutation = gql`
                                    mutation UpdateUser(
                                      $input: UpdateUserInput!
                                    ) {
                                      updateUser(input: $input) {
                                        id
                                        status
                                        isDeleted
                                      }
                                    }
                                  `;
                                  await API.graphql({
                                    query: updateMutation,
                                    variables: {
                                      input: {
                                        id: user.id,
                                        status: 'deleted',
                                        isDeleted: true,
                                      },
                                    },
                                  });
                                  await getOrgMembers();
                                } catch (err) {
                                  console.error('Error deleting user:', err);
                                  setExemptError('Failed to delete user.');
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Add Existing User as Exempt */}
                  <Box style={{ margin: '15px 0' }}>
                    <Text style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                      Mark Existing User as Exempt
                    </Text>
                    <Autocomplete
                      options={nonExemptUsers}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.email})`
                      }
                      onChange={async (event, value) => {
                        if (!value) return;
                        try {
                          const updateMutation = gql`
                            mutation UpdateUser($input: UpdateUserInput!) {
                              updateUser(input: $input) {
                                id
                                excludeFromBilling
                              }
                            }
                          `;
                          await API.graphql({
                            query: updateMutation,
                            variables: {
                              input: { id: value.id, excludeFromBilling: true },
                            },
                          });
                          await getOrgMembers();
                        } catch (err) {
                          console.error('Error marking user as exempt:', err);
                          setExemptError(
                            'Failed to mark user as billing-exempt.'
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search users..."
                          variant="outlined"
                          size="small"
                        />
                      )}
                      style={{ maxWidth: '400px' }}
                      value={null}
                      blurOnSelect
                      clearOnBlur
                    />
                  </Box>

                  {/* Add New Exempt User Form */}
                  <Box
                    style={{
                      margin: '15px 0',
                      borderTop: '1px solid #eee',
                      paddingTop: '15px',
                    }}
                  >
                    <Text style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                      Add New Billing-Exempt User
                    </Text>
                    <Box
                      style={{
                        display: 'flex',
                        gap: '10px',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      <TextField
                        label="First Name"
                        variant="outlined"
                        size="small"
                        value={exemptFirstName}
                        onChange={(e) => setExemptFirstName(e.target.value)}
                        style={{ width: '160px' }}
                      />
                      <TextField
                        label="Last Name"
                        variant="outlined"
                        size="small"
                        value={exemptLastName}
                        onChange={(e) => setExemptLastName(e.target.value)}
                        style={{ width: '160px' }}
                      />
                      <TextField
                        label="Email"
                        variant="outlined"
                        size="small"
                        value={exemptEmail}
                        onChange={(e) => setExemptEmail(e.target.value)}
                        style={{ width: '220px' }}
                      />
                      <TextField
                        label="Phone Number"
                        variant="outlined"
                        size="small"
                        value={exemptPhone}
                        onChange={(e) => setExemptPhone(e.target.value)}
                        style={{ width: '160px' }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          exemptLoading ||
                          !exemptFirstName ||
                          !exemptLastName ||
                          !exemptEmail
                        }
                        onClick={async () => {
                          setExemptLoading(true);
                          setExemptError('');
                          setExemptSuccess('');
                          try {
                            // Generate random 16-char password meeting Cognito requirements
                            const chars = 'abcdefghijklmnopqrstuvwxyz';
                            const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
                            const digits = '0123456789';
                            const symbols = '!@#$%^&*()_+-=';
                            const all = chars + upper + digits + symbols;
                            const arr = new Uint8Array(16);
                            crypto.getRandomValues(arr);
                            const password = [
                              chars[arr[0] % chars.length],
                              upper[arr[1] % upper.length],
                              digits[arr[2] % digits.length],
                              symbols[arr[3] % symbols.length],
                            ];
                            for (let i = 4; i < 16; i++) {
                              password.push(all[arr[i] % all.length]);
                            }
                            // Shuffle
                            for (let i = password.length - 1; i > 0; i--) {
                              const j = arr[i] % (i + 1);
                              [password[i], password[j]] = [
                                password[j],
                                password[i],
                              ];
                            }
                            const generatedPassword = password.join('');

                            const response = await fetch(
                              'https://api.gogamify.com/signup/',
                              {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  firstName: exemptFirstName,
                                  lastName: exemptLastName,
                                  email: exemptEmail,
                                  password: generatedPassword,
                                  phoneNumber: exemptPhone,
                                  orgID,
                                  excludeFromBilling: true,
                                  minimal: false,
                                  goals: ['billing-exempt'],
                                  website: 'configurator',
                                }),
                              }
                            );
                            const data = await response.json();
                            if (!response.ok) {
                              throw new Error(
                                data.message || data.error || 'Signup failed'
                              );
                            }
                            setExemptFirstName('');
                            setExemptLastName('');
                            setExemptEmail('');
                            setExemptPhone('');
                            setExemptSuccess('User created successfully.');
                            setTimeout(() => setExemptSuccess(''), 4000);
                            await getOrgMembers();
                          } catch (err) {
                            console.error('Error creating exempt user:', err);
                            setExemptError(
                              err.message || 'Failed to create user.'
                            );
                          } finally {
                            setExemptLoading(false);
                          }
                        }}
                      >
                        {exemptLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          'Add User'
                        )}
                      </Button>
                    </Box>
                  </Box>
                </>
              );
            })()}
          </Box>
        )}{' '}
        {/* end !bypassBillingCheck */}
        {/* Exempt user error/success alerts */}
        <Snackbar
          open={!!exemptError}
          autoHideDuration={6000}
          onClose={() => setExemptError('')}
        >
          <Alert
            onClose={() => setExemptError('')}
            severity="error"
            sx={{ width: '100%' }}
          >
            {exemptError}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!exemptSuccess}
          autoHideDuration={4000}
          onClose={() => setExemptSuccess('')}
        >
          <Alert
            onClose={() => setExemptSuccess('')}
            severity="success"
            sx={{ width: '100%' }}
          >
            {exemptSuccess}
          </Alert>
        </Snackbar>
        {!bypassBillingCheck && (
          <Box
            style={{
              marginTop: 20,
              borderTop: '1px solid #ddd',
              paddingTop: '15px',
            }}
          >
            <Text
              style={{
                fontWeight: 'bold',
                fontSize: '1.2rem',
                marginBottom: '10px',
              }}
            >
              Trials
            </Text>
            {/* Trials table */}
            <Box style={{ marginTop: 15 }}>
              {/* Column headers */}
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: '5px',
                  gap: 10,
                }}
              >
                <Text style={{ width: '14vw', fontWeight: 'bold' }}>
                  Start Date
                </Text>
                <Text style={{ width: '10vw', fontWeight: 'bold' }}>
                  Estimates
                </Text>
                <Text style={{ width: '14vw', fontWeight: 'bold' }}>
                  Measurement Reports
                </Text>
                <Text style={{ width: '8vw', fontWeight: 'bold' }}>
                  Length (days)
                </Text>
              </Box>
              {/* Empty state */}
              {trials.length === 0 && (
                <Text style={{ color: '#888', margin: '5px' }}>
                  No trials configured.
                </Text>
              )}
              {/* Trial rows */}
              {trials.map((trial, i) => (
                <Box
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: '5px',
                    gap: 10,
                  }}
                >
                  <TextField
                    variant="standard"
                    value={trial.startDate}
                    placeholder="YYYY-MM-DD"
                    style={{ width: '14vw' }}
                    onChange={(e) => {
                      const updated = [...trials];
                      updated[i] = { ...updated[i], startDate: e.target.value };
                      setTrials(updated);
                    }}
                  />
                  <TextField
                    variant="standard"
                    type="number"
                    value={trial.estimatesCount}
                    style={{ width: '10vw' }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const updated = [...trials];
                      updated[i] = {
                        ...updated[i],
                        estimatesCount:
                          raw === ''
                            ? ''
                            : Math.max(0, Math.floor(Number(raw))),
                      };
                      setTrials(updated);
                    }}
                  />
                  <TextField
                    variant="standard"
                    type="number"
                    value={trial.measurementReportsCount}
                    style={{ width: '14vw' }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const updated = [...trials];
                      updated[i] = {
                        ...updated[i],
                        measurementReportsCount:
                          raw === ''
                            ? ''
                            : Math.max(0, Math.floor(Number(raw))),
                      };
                      setTrials(updated);
                    }}
                  />
                  <TextField
                    variant="standard"
                    type="number"
                    value={trial.length}
                    style={{ width: '8vw' }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const updated = [...trials];
                      updated[i] = {
                        ...updated[i],
                        length:
                          raw === ''
                            ? ''
                            : Math.max(1, Math.floor(Number(raw))),
                      };
                      setTrials(updated);
                    }}
                  />
                  <IconButton
                    onClick={() =>
                      setTrials(trials.filter((_, idx) => idx !== i))
                    }
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
              {/* Add Trial button */}
              <Box style={{ marginTop: 8 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    setTrials([
                      ...trials,
                      {
                        startDate: '',
                        estimatesCount: 0,
                        measurementReportsCount: 0,
                        length: 7,
                      },
                    ])
                  }
                >
                  Add Trial
                </Button>
              </Box>
              {/* Validation error */}
              {trialsError && (
                <Text style={{ color: 'red', marginTop: 5 }}>
                  {trialsError}
                </Text>
              )}
            </Box>
          </Box>
        )}{' '}
        {/* end !bypassBillingCheck trials */}
      </Box>{' '}
      {/* end billing card */}
      <Card id="features-box">
        <Box className="center-content">
          <Text
            onClick={() => {
              getOrgMembers();
            }}
            style={{ fontWeight: 'bold', fontSize: '1.8rem', color: '#FF6A00' }}
          >
            FEATURES
          </Text>
        </Box>
        <Box id="pin-dropping-box">
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <Text style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
              Canvassing
            </Text>
            <Switch
              checked={featuresList.route.enabled}
              onChange={() =>
                setFeaturesList(() => ({
                  ...featuresList,
                  route: {
                    ...featuresList.route,
                    enabled: !featuresList.route.enabled,
                  },
                }))
              }
            />
          </Box>
          {featuresList.route.enabled && (
            <Box>
              {/* <StageDispositionsConfig orgID={orgID} /> */}
              <Box
                style={{
                  display: 'flex',
                  gap: 55,
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text>Only creator can change disposition</Text>
                    <Switch
                      disabled={!featuresList.route.enabled}
                      checked={
                        featuresList.route.onlyCreatorCanChangeDisposition
                      }
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          route: {
                            ...featuresList.route,
                            onlyCreatorCanChangeDisposition:
                              !featuresList.route
                                .onlyCreatorCanChangeDisposition,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text>Only creator can delete disposition</Text>
                    <Switch
                      disabled={!featuresList.route.enabled}
                      checked={featuresList.route.onlyCreatorCanDelete}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          route: {
                            ...featuresList.route,
                            onlyCreatorCanDelete:
                              !featuresList.route.onlyCreatorCanDelete,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text>Only creator can edit a deal</Text>
                    <Switch
                      disabled={!featuresList.route.enabled}
                      checked={featuresList.route?.onlyCreatorCanEdit || false}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          route: {
                            ...featuresList.route,
                            onlyCreatorCanEdit:
                              !featuresList.route?.onlyCreatorCanEdit,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text>Only creator can view details</Text>
                    <Switch
                      disabled={!featuresList.route.enabled}
                      checked={featuresList.route.onlyCreatorCanViewDetails}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          route: {
                            ...featuresList.route,
                            onlyCreatorCanViewDetails:
                              !featuresList.route.onlyCreatorCanViewDetails,
                          },
                        }))
                      }
                    />
                  </Box>
                </Box>
                <Box>
                  <Box
                    name="enhance-door-data"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text>Enhance Door Data</Text>
                    <Switch
                      disabled={!featuresList.route.enabled}
                      checked={featuresList.route.enhancedDoorData}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          route: {
                            ...featuresList.route,
                            enhancedDoorData:
                              !featuresList.route.enhancedDoorData,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    name="enable-wandermode"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text>Wander mode Enabled</Text>
                    <Switch
                      disabled={!featuresList.route.enabled}
                      checked={featuresList.route.wanderMode || false}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          route: {
                            ...featuresList.route,
                            wanderMode: !featuresList.route.wanderMode,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    name="disable-search"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text>Disable Search</Text>
                    <Switch
                      disabled={!featuresList.route.enabled}
                      checked={featuresList.route.disableSearch}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          route: {
                            ...featuresList.route,
                            disableSearch: !featuresList.route.disableSearch,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    name="allow-export-pins"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text>Allow Export Pins</Text>
                    <Switch
                      disabled={!featuresList.route.enabled}
                      checked={featuresList.route.allowExportPins || false}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          route: {
                            ...featuresList.route,
                            allowExportPins:
                              !featuresList.route.allowExportPins,
                          },
                        }))
                      }
                    />
                  </Box>
                </Box>
                <Box>
                  <Box
                    name="radius-box"
                    style={{ display: 'flex', width: '30vw', gap: 25 }}
                  >
                    <Text>Radius</Text>
                    <Slider
                      disabled={!featuresList.route.enabled}
                      valueLabelDisplay="auto"
                      min={1000}
                      max={5000}
                      step={100}
                      value={featuresList.route.radius}
                      onChange={(e, newValue) =>
                        setFeaturesList((prevState) => ({
                          ...prevState,
                          route: {
                            ...prevState.route,
                            radius: newValue,
                          },
                        }))
                      }
                    />
                    <Text style={{ whiteSpace: 'nowrap' }}>
                      {featuresList.route.radius + ' M'}
                    </Text>
                  </Box>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        !featuresList.route.enabled
                          ? null
                          : setFeaturesList(() => ({
                              ...featuresList,
                              route: {
                                ...featuresList.route,
                                maxPinDistanceCheck:
                                  !featuresList.route.maxPinDistanceCheck,
                              },
                            }))
                      }
                    >
                      Max Pin Distance Check
                    </Text>
                    <Switch
                      disabled={!featuresList.route.enabled}
                      checked={featuresList.route.maxPinDistanceCheck}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          route: {
                            ...featuresList.route,
                            maxPinDistanceCheck:
                              !featuresList.route.maxPinDistanceCheck,
                          },
                        }))
                      }
                    />
                  </Box>
                  {featuresList.route.maxPinDistanceCheck && (
                    <Box
                      name="max-pin-distance"
                      style={{ display: 'flex', width: '30vw', gap: 25 }}
                    >
                      <Text>Max Pin Distance</Text>
                      <Slider
                        disabled={!featuresList.route.enabled}
                        valueLabelDisplay="auto"
                        min={10}
                        max={50}
                        step={10}
                        value={featuresList.route.maxPinDistance}
                        onChange={(e, newValue) =>
                          setFeaturesList((prevState) => ({
                            ...prevState,
                            route: {
                              ...prevState.route,
                              maxPinDistance: newValue,
                            },
                          }))
                        }
                      />
                      <Text style={{ whiteSpace: 'nowrap' }}>
                        {featuresList.route.maxPinDistance + ' M'}
                      </Text>
                    </Box>
                  )}
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: 10,
                    }}
                  >
                    <Text>Rep Locator</Text>
                    <Select
                      disabled={!featuresList.route.enabled}
                      value={featuresList.route.repLocator || 'none'}
                      onChange={(e) =>
                        setFeaturesList((prevState) => ({
                          ...prevState,
                          route: {
                            ...prevState.route,
                            repLocator: e.target.value,
                          },
                        }))
                      }
                      className="config-select-field"
                    >
                      {['None', 'User', 'Manager', 'Admin'].map((el, i) => (
                        <MenuItem
                          value={el.toLowerCase()}
                          key={i}
                          style={{ backgroundColor: 'white' }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = 'lightgray')
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = 'white')
                          }
                        >
                          {el}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        <Box id="feed-box">
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text
              style={{
                fontWeight: 'bold',
                fontSize: '1.5rem',
              }}
            >
              Default Schedule
            </Text>
            {!defaultScheduleObj?.default && (
              <Box
                className="create-default-schedule-button"
                onClick={() => setDefaultScheduleObj(defaultScheduleTemp)}
              >
                Create default schedule
              </Box>
            )}
          </Box>
          {defaultScheduleObj?.default && (
            <Box>
              <Box>
                <Text style={{ fontWeight: 'bold' }}>Available Days</Text>
                <Box style={{ display: 'flex', gap: 5 }}>
                  {[
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                    'Sunday',
                  ].map((day, index) => (
                    <Box
                      key={index}
                      onClick={() => {
                        const temp = [...defaultScheduleObj.default[0].days];
                        if (temp.includes(index)) {
                          const ind = temp.indexOf(index);
                          temp.splice(ind, 1);
                        } else {
                          temp.push(index);
                          temp.sort();
                        }
                        setDefaultScheduleObj((p) => ({
                          default: [{ ...p.default[0], days: temp }],
                        }));
                      }}
                      className={`default-schedule-day-single-box ${
                        defaultScheduleObj.default[0].days.includes(index)
                          ? ''
                          : 'un'
                      }selected-single-day`}
                    >
                      {day}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box
                style={{
                  width: '100%',
                  display: 'flex',
                  gap: '3%',
                  marginTop: 5,
                }}
              >
                <Box style={{ width: '24%' }}>
                  <Box
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Default Meeting Length
                    </Text>
                    <Text>
                      {defaultScheduleObj.default[0].defaultMeetingLength}
                    </Text>
                  </Box>
                  <Slider
                    min={15}
                    max={360}
                    step={15}
                    value={
                      defaultScheduleObj.default[0].defaultMeetingLength || 0
                    }
                    onChange={(e, newVal) => {
                      setDefaultScheduleObj((p) => ({
                        default: [
                          { ...p.default[0], defaultMeetingLength: newVal },
                        ],
                      }));
                    }}
                  />
                </Box>
                <Box style={{ width: '24%' }}>
                  <Box
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Minimum Meeting Length
                    </Text>
                    <Text>
                      {defaultScheduleObj.default[0].minimumMeetingLength}
                    </Text>
                  </Box>
                  <Slider
                    min={15}
                    max={1000}
                    step={15}
                    value={
                      defaultScheduleObj.default[0].minimumMeetingLength || 0
                    }
                    onChange={(e, newVal) => {
                      setDefaultScheduleObj((p) => ({
                        default: [
                          { ...p.default[0], minimumMeetingLength: newVal },
                        ],
                      }));
                    }}
                  />
                </Box>
                <Box style={{ width: '24%' }}>
                  <Box
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Maximum Meeting Length
                    </Text>
                    <Text>
                      {defaultScheduleObj.default[0].maximumMeetingLength}
                    </Text>
                  </Box>
                  <Slider
                    min={15}
                    max={1000}
                    step={30}
                    value={
                      defaultScheduleObj.default[0].maximumMeetingLength || 0
                    }
                    onChange={(e, newVal) => {
                      setDefaultScheduleObj((p) => ({
                        default: [
                          { ...p.default[0], maximumMeetingLength: newVal },
                        ],
                      }));
                    }}
                  />
                </Box>
                <Box style={{ width: '24%' }}>
                  <Box
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Minutes Between Meetings
                    </Text>
                    <Text>
                      {defaultScheduleObj.default[0].minutesBetweenMeetings}
                    </Text>
                  </Box>
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={
                      defaultScheduleObj.default[0].minutesBetweenMeetings || 0
                    }
                    onChange={(e, newVal) => {
                      setDefaultScheduleObj((p) => ({
                        default: [
                          { ...p.default[0], minutesBetweenMeetings: newVal },
                        ],
                      }));
                    }}
                  />
                </Box>
              </Box>
              <Box style={{ display: 'flex', gap: '10%' }}>
                <Box style={{ width: '45%' }}>
                  <Box style={{ display: 'flex', gap: 10 }}>
                    <Text style={{ fontWeight: 'bold' }}>Start Time</Text>
                    <Text>
                      {formatTime(defaultScheduleObj.default[0].startTime)}
                    </Text>
                  </Box>
                  <Slider
                    min={0}
                    max={1439}
                    step={5}
                    value={defaultScheduleObj.default[0].startTime || 0}
                    onChange={(e, newVal) => {
                      setDefaultScheduleObj((p) => ({
                        default: [{ ...p.default[0], startTime: newVal }],
                      }));
                    }}
                  />
                </Box>
                <Box style={{ width: '45%' }}>
                  <Box style={{ display: 'flex', gap: 10 }}>
                    <Text style={{ fontWeight: 'bold' }}>End Time</Text>
                    <Text>
                      {formatTime(defaultScheduleObj.default[0].endTime)}
                    </Text>
                  </Box>
                  <Slider
                    min={0}
                    max={1439}
                    step={5}
                    value={defaultScheduleObj.default[0].endTime || 0}
                    onChange={(e, newVal) => {
                      setDefaultScheduleObj((p) => ({
                        default: [{ ...p.default[0], endTime: newVal }],
                      }));
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        {featureFlag('recording') ||
          (featureFlag('recordingScreen') && (
            <Box id="feed-box" style={{ display: 'flex', gap: 10 }}>
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Recording Consent Required
              </Text>
              <Switch
                checked={
                  featuresList.autoRecording
                    ? !featuresList.autoRecording
                    : true
                }
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    autoRecording: !featuresList.autoRecording,
                  }))
                }
              />
            </Box>
          ))}
        <Box id="feed-features-box">
          <Box id="feed-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Feed
              </Text>
              <Switch
                checked={featuresList.feed.enabled}
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    feed: {
                      ...featuresList.feed,
                      enabled: !featuresList.feed.enabled,
                    },
                  }))
                }
              />
            </Box>
            {featuresList.feed.enabled && (
              <>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Comment</Text>
                  <Switch
                    checked={featuresList.feed.comment}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        feed: {
                          ...featuresList.feed,
                          comment: !featuresList.feed.comment,
                        },
                      }))
                    }
                  />
                </Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Like</Text>
                  <Switch
                    checked={featuresList.feed.like}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        feed: {
                          ...featuresList.feed,
                          like: !featuresList.feed.like,
                        },
                      }))
                    }
                  />
                </Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Upload Media</Text>
                  <Switch
                    checked={featuresList.feed.uploadVideo}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        feed: {
                          ...featuresList.feed,
                          uploadVideo: !featuresList.feed.uploadVideo,
                        },
                      }))
                    }
                  />
                </Box>
              </>
            )}
          </Box>

          <Box id="feedblast-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Feed Blast
              </Text>
              <Switch
                checked={featuresList.feedBlast.enabled}
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    feedBlast: {
                      ...featuresList.feedBlast,
                      enabled: !featuresList.feedBlast.enabled,
                    },
                  }))
                }
              />
            </Box>
            {featuresList.feedBlast.enabled && (
              <Box style={{ display: 'flex', gap: '10px' }}>
                <Box>
                  <Box className="flex-centered space-b g-hov">
                    <Text style={{ fontWeight: 'bold' }}>Admin Only</Text>
                    <Switch
                      checked={featuresList.feedBlast.adminOnly}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          feedBlast: {
                            ...featuresList.feedBlast,
                            adminOnly: !featuresList.feedBlast.adminOnly,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box className="flex-centered space-b g-hov">
                    <Text style={{ fontWeight: 'bold' }}>
                      Default Post To All
                    </Text>
                    <Switch
                      checked={featuresList.feedBlast.deafultPostToAll}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          feedBlast: {
                            ...featuresList.feedBlast,
                            deafultPostToAll:
                              !featuresList.feedBlast.deafultPostToAll,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box className="flex-centered space-b g-hov">
                    <Text style={{ fontWeight: 'bold' }}>Schedule Send</Text>
                    <Switch
                      checked={featuresList.feedBlast.scheduleSend}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          feedBlast: {
                            ...featuresList.feedBlast,
                            scheduleSend: !featuresList.feedBlast.scheduleSend,
                          },
                        }))
                      }
                    />
                  </Box>
                </Box>
                <Box className="w60">
                  <Box className="flex-centered">
                    <Text style={{ fontWeight: 'bold' }}>Circle</Text>
                    <Select
                      //   className="select-comp"
                      value={featuresList.feedBlast.circle}
                      onChange={(e) =>
                        setFeaturesList((prevState) => ({
                          ...prevState,
                          feedBlast: {
                            ...prevState.feedBlast,
                            circle: e.target.value,
                          },
                        }))
                      }
                      label="Circle"
                      className="config-select-field"
                      // style={{ backgroundColor: 'white', border: 'none', }}
                    >
                      {['User', 'Moderator', 'Admin'].map((el, i) => (
                        <MenuItem
                          value={el.toLowerCase()}
                          key={i}
                          style={{
                            backgroundColor: 'white',
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = 'lightgray')
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = 'white')
                          }
                        >
                          {el}
                        </MenuItem>
                      ))}
                      {/* <MenuItem value="user">User</MenuItem>
                      <MenuItem value="moderator">Moderator</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem> */}
                    </Select>
                  </Box>
                  <Box className="flex-centered">
                    <Text style={{ fontWeight: 'bold' }}>Individual</Text>
                    <Select
                      value={featuresList.feedBlast.individual}
                      onChange={(e) =>
                        setFeaturesList((prevState) => ({
                          ...prevState,
                          feedBlast: {
                            ...prevState.feedBlast,
                            individual: e.target.value,
                          },
                        }))
                      }
                      label="Individual"
                      className="config-select-field"
                      // style={{ backgroundColor: 'white', border: 'none', }}
                    >
                      {['User', 'Moderator', 'Admin'].map((el, i) => (
                        <MenuItem
                          value={el.toLowerCase()}
                          key={i}
                          style={{
                            backgroundColor: 'white',
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = 'lightgray')
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = 'white')
                          }
                        >
                          {el}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box id="features-grid">
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Person
              </Text>
              <Switch
                checked={featuresList.person.enabled}
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    person: {
                      ...featuresList.person,
                      enabled: !featuresList.person.enabled,
                    },
                  }))
                }
              />
            </Box>
            {featuresList.person.enabled && (
              <Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Upload Picture</Text>
                  <Switch
                    checked={featuresList.person.uploadPicture}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        person: {
                          ...featuresList.person,
                          uploadPicture: !featuresList.person.uploadPicture,
                        },
                      }))
                    }
                  />
                </Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Upload Media</Text>
                  <Switch
                    checked={featuresList.person.uploadVideo}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        person: {
                          ...featuresList.person,
                          uploadVideo: !featuresList.person.uploadVideo,
                        },
                      }))
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Circle
              </Text>
              <Switch
                checked={featuresList.circle.enabled}
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    circle: {
                      ...featuresList.circle,
                      enabled: !featuresList.circle.enabled,
                    },
                  }))
                }
              />
            </Box>
            {featuresList.circle.enabled && (
              <Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Upload Picture</Text>
                  <Switch
                    checked={featuresList.circle.uploadPicture}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        circle: {
                          ...featuresList.circle,
                          uploadPicture: !featuresList.circle.uploadPicture,
                        },
                      }))
                    }
                  />
                </Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Upload Media</Text>
                  <Switch
                    checked={featuresList.circle.uploadVideo}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        circle: {
                          ...featuresList.circle,
                          uploadVideo: !featuresList.circle.uploadVideo,
                        },
                      }))
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Reward
              </Text>
              <Switch
                checked={featuresList.reward.enabled}
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    reward: {
                      ...featuresList.reward,
                      enabled: !featuresList.reward.enabled,
                    },
                  }))
                }
              />
            </Box>
            {featuresList.reward.enabled && (
              <Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Upload Picture</Text>
                  <Switch
                    checked={featuresList.reward.uploadPicture}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        reward: {
                          ...featuresList.reward,
                          uploadPicture: !featuresList.reward.uploadPicture,
                        },
                      }))
                    }
                  />
                </Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Upload Media</Text>
                  <Switch
                    checked={featuresList.reward.uploadVideo}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        reward: {
                          ...featuresList.reward,
                          uploadVideo: !featuresList.reward.uploadVideo,
                        },
                      }))
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Deal
              </Text>
              <Switch
                checked={featuresList.deal.enabled}
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    deal: {
                      ...featuresList.deal,
                      enabled: !featuresList.deal.enabled,
                    },
                  }))
                }
              />
            </Box>
            {featuresList.deal.enabled && (
              <Box>
                <Box style={{ display: 'flex', gap: 15 }}>
                  <Box
                    className="flex-centered space-b g-hov"
                    style={{ width: '50%' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Disable Circle Filter
                    </Text>
                    <Switch
                      checked={featuresList.deal.disableCircleFilter}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          deal: {
                            ...featuresList.deal,
                            disableCircleFilter:
                              !featuresList.deal.disableCircleFilter,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    className="flex-centered space-b g-hov"
                    style={{ width: '50%' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Disable User Filter
                    </Text>
                    <Switch
                      checked={featuresList.deal.disableUserFilter}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          deal: {
                            ...featuresList.deal,
                            disableUserFilter:
                              !featuresList.deal.disableUserFilter,
                          },
                        }))
                      }
                    />
                  </Box>
                </Box>
                <Box style={{ display: 'flex', gap: 15 }}>
                  <Box
                    className="flex-centered space-b g-hov"
                    style={{ width: '50%' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>Sign On Device</Text>
                    <Switch
                      checked={featuresList.deal.signOnDevice || false}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          deal: {
                            ...featuresList.deal,
                            signOnDevice: !featuresList.deal.signOnDevice,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    className="flex-centered space-b g-hov"
                    style={{ width: '50%' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>Sign Using Email</Text>
                    <Switch
                      checked={featuresList.deal.signUsingEmail || false}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          deal: {
                            ...featuresList.deal,
                            signUsingEmail: !featuresList.deal.signUsingEmail,
                          },
                        }))
                      }
                    />
                  </Box>
                </Box>
                <Box style={{ display: 'flex', gap: 15 }}>
                  <Box
                    className="flex-centered space-b g-hov"
                    style={{ width: '50%' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Disable Change Stage
                    </Text>
                    <Switch
                      checked={featuresList.deal.disableChangeStage}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          deal: {
                            ...featuresList.deal,
                            disableChangeStage:
                              !featuresList.deal.disableChangeStage,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    className="flex-centered space-b g-hov"
                    style={{ width: '50%' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Order Lines Required to Sign
                    </Text>
                    <Switch
                      checked={featuresList.deal.noOrderLinesRequiredForSigning}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          deal: {
                            ...featuresList.deal,
                            noOrderLinesRequiredForSigning:
                              !featuresList.deal.noOrderLinesRequiredForSigning,
                          },
                        }))
                      }
                    />
                  </Box>
                </Box>
                <Box style={{ display: 'flex', gap: 15 }}>
                  <Box
                    className="flex-centered space-b g-hov"
                    style={{ width: '50%' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Closer Equals Setter
                    </Text>
                    <Switch
                      checked={featuresList.deal.closerEqualsSetter || false}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          deal: {
                            ...featuresList.deal,
                            closerEqualsSetter:
                              !featuresList.deal.closerEqualsSetter,
                          },
                        }))
                      }
                    />
                  </Box>
                  <Box
                    className="flex-centered space-b g-hov"
                    style={{ width: '50%' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>
                      Show Disposition On List
                    </Text>
                    <Switch
                      checked={featuresList.deal.showDispositionOnDealsList}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          deal: {
                            ...featuresList.deal,
                            showDispositionOnDealsList:
                              !featuresList.deal.showDispositionOnDealsList,
                          },
                        }))
                      }
                    />
                  </Box>
                </Box>
                <Box style={{ display: 'flex', gap: 15 }}>
                  <Box
                    className="flex-centered space-b g-hov"
                    style={{ width: '50%' }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>Track Deal Views</Text>
                    <Switch
                      checked={featuresList.deal.trackDealViews || false}
                      onChange={() =>
                        setFeaturesList(() => ({
                          ...featuresList,
                          deal: {
                            ...featuresList.deal,
                            trackDealViews: !featuresList.deal.trackDealViews,
                          },
                        }))
                      }
                    />
                  </Box>
                  {featuresList.deal.trackDealViews && (
                    <Box
                      className="flex-centered space-b"
                      style={{ width: '50%' }}
                    >
                      <Text style={{ fontWeight: 'bold' }}>
                        View Debounce (minutes)
                      </Text>
                      <TextField
                        style={{ width: '80px' }}
                        variant="standard"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={
                          featuresList.deal.trackDealViewsDebounceMinutes ?? 3
                        }
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          setFeaturesList(() => ({
                            ...featuresList,
                            deal: {
                              ...featuresList.deal,
                              trackDealViewsDebounceMinutes: isNaN(val)
                                ? 0
                                : Math.max(0, val),
                            },
                          }));
                        }}
                      />
                    </Box>
                  )}
                </Box>

                <Box>
                  <Text style={{ fontWeight: 'bold' }}>Order Table Fields</Text>
                  <Box
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 4,
                      border: '1px solid gray',
                      padding: 5,
                      borderRadius: 4,
                    }}
                  >
                    {[
                      { value: 'header', label: 'Header' },
                      { value: 'sellToInfo', label: 'Sell To Info' },
                      { value: 'quantity', label: 'Quantity' },
                      { value: 'description', label: 'Description' },
                      { value: 'unitPrice', label: 'Unit Price' },
                      { value: 'discount', label: 'Discount' },
                      { value: 'extPrice', label: 'Ext Price' },
                      { value: 'totalRow', label: 'Total Row' },
                    ].map(({ label, value }, i) => (
                      <Box
                        key={i}
                        className="hov"
                        onClick={() => {
                          if (!featuresList.deal.orderTableFields) {
                            setFeaturesList((p) => ({
                              ...p,
                              deal: {
                                ...p.deal,
                                orderTableFields: [value],
                              },
                            }));
                            return;
                          }
                          const index =
                            featuresList.deal.orderTableFields.indexOf(value);
                          if (index === -1) {
                            setFeaturesList((p) => ({
                              ...p,
                              deal: {
                                ...p.deal,
                                orderTableFields: [
                                  ...p.deal.orderTableFields,
                                  value,
                                ],
                              },
                            }));
                          } else {
                            const newFeatures = { ...featuresList };
                            newFeatures.deal.orderTableFields.splice(index, 1);
                            setFeaturesList(newFeatures);
                          }
                        }}
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          backgroundColor:
                            featuresList.deal.orderTableFields?.includes(value)
                              ? '#6177c1'
                              : 'lightgray',
                        }}
                      >
                        <Text color="white">{label}</Text>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Schedule
              </Text>
              <Switch
                checked={featuresList.schedule.enabled}
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    schedule: {
                      ...featuresList.schedule,
                      enabled: !featuresList.schedule.enabled,
                    },
                  }))
                }
              />
            </Box>
            {featuresList.schedule.enabled && (
              <>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Auto Schedule</Text>
                  <Switch
                    checked={featuresList.schedule.autoSchedule || false}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        schedule: {
                          ...featuresList.schedule,
                          autoSchedule: !featuresList.schedule.autoSchedule,
                        },
                      }))
                    }
                  />
                </Box>
                {featuresList?.schedule?.autoSchedule && (
                  <Box>
                    {fetchingData || !orgUSers ? (
                      <Text>LOADING USERS LIST</Text>
                    ) : (
                      <Box>
                        <Box>
                          <Text style={{ fontWeight: 'bold' }}>
                            Auto Assign to Role
                          </Text>
                          <Box id="roles-buttons-container">
                            {orgRoles.map((role, i) => {
                              const isSelected =
                                role.id ===
                                featuresList.schedule.autoAssignRoleID;
                              return (
                                <Box
                                  onClick={() => {
                                    if (isSelected) {
                                      setFeaturesList((p) => ({
                                        ...p,
                                        schedule: {
                                          ...p.schedule,
                                          autoAssignRoleID: null,
                                        },
                                      }));
                                    } else {
                                      setFeaturesList((p) => ({
                                        ...p,
                                        schedule: {
                                          ...p.schedule,
                                          autoAssignRoleID: role.id,
                                        },
                                      }));
                                    }
                                  }}
                                  key={i}
                                  style={{
                                    padding: '3px',
                                    borderRadius: '4px',
                                    border: '1px solid black',
                                    color: 'white',
                                    backgroundColor: isSelected
                                      ? '#6177c1'
                                      : 'gray',
                                  }}
                                >
                                  <Text color="white">{role.roleName}</Text>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                        <Text style={{ fontWeight: 'bold' }}>
                          Auto Assign to user
                        </Text>
                        <Select
                          placeholder="pick something!"
                          style={{ width: '100%' }}
                          value={featuresList.schedule.automaticCloserID || ''}
                          onChange={(e) =>
                            setFeaturesList((p) => ({
                              ...p,
                              schedule: {
                                ...p.schedule,
                                automaticCloserID: e.target.value,
                              },
                            }))
                          }
                          MenuProps={{
                            style: {
                              zIndex: 100002,
                            },
                            MenuListProps: {
                              sx: {
                                maxHeight: 300,
                                overflowY: 'auto',
                                paddingTop: 0,
                                paddingBottom: 0,
                                borderRadius: '8px',
                                border: '1px solid #D0D2D8',
                              },
                            },
                          }}
                        >
                          <MenuItem
                            value={''}
                            style={{
                              backgroundColor:
                                featuresList?.schedule?.automaticCloserID ===
                                  '' ||
                                !featuresList?.schedule?.automaticCloserID
                                  ? 'lightblue'
                                  : 'white',
                            }}
                          >
                            {'Select scheduler'}
                          </MenuItem>
                          {orgUSers.map((user) => (
                            <MenuItem
                              value={user.id}
                              key={user.id}
                              style={{
                                backgroundColor:
                                  user.id ===
                                  featuresList.schedule.automaticCloserID
                                    ? 'lightblue'
                                    : 'white',
                              }}
                            >
                              {user.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}
                  </Box>
                )}
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Use Round Robin</Text>
                  <Switch
                    checked={featuresList.schedule.useRoundRobin || false}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        schedule: {
                          ...featuresList.schedule,
                          useRoundRobin: !featuresList.schedule.useRoundRobin,
                        },
                      }))
                    }
                  />
                </Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Scheduler Role</Text>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flex: 1,
                      border: '1px solid lightgray',
                      margin: '0 5px',
                      borderRadius: 4,
                      padding: 5,
                    }}
                  >
                    {/* {featuresList.schedule.schedulerRole?.map((role, i) => ( */}
                    {['scheduler', 'manager', 'setter', 'closer'].map(
                      (role, i) => {
                        const roleActive =
                          featuresList.schedule.schedulerRole?.includes(role);
                        return (
                          <Text
                            className="hov"
                            style={{
                              margin: '0 4px',
                              backgroundColor: roleActive
                                ? '#6177c1'
                                : 'lightgray',
                              borderRadius: 4,
                              padding: '2px 5px',
                              color: 'white',
                            }}
                            onClick={() => {
                              const temp = featuresList.schedule.schedulerRole
                                ? [...featuresList.schedule.schedulerRole]
                                : [];
                              if (roleActive) {
                                const index = temp.indexOf(role);
                                temp.splice(index, 1);
                              } else {
                                temp.push(role);
                              }
                              setFeaturesList((p) => ({
                                ...p,
                                schedule: {
                                  ...p.schedule,
                                  schedulerRole: temp,
                                },
                              }));
                            }}
                            key={i}
                          >
                            {role}
                          </Text>
                        );
                      }
                    )}
                  </Box>
                </Box>
                <Box
                  className="flex-centered space-b g-hov"
                  style={{ margin: '10px 0px' }}
                >
                  <Text style={{ fontWeight: 'bold' }}>SMS Notifications</Text>
                  <Box>
                    <Switch
                      checked={
                        featuresList.schedule.appointmentNotifications
                          ?.enabled || false
                      }
                      onChange={() => {
                        const currentNotifications = featuresList.schedule
                          ?.appointmentNotifications || {
                          enabled: false,
                          triggers: [],
                        };
                        setFeaturesList((prev) => ({
                          ...prev,
                          schedule: {
                            ...prev.schedule,
                            appointmentNotifications: {
                              enabled: !currentNotifications.enabled,
                              triggers: [...currentNotifications.triggers],
                            },
                          },
                        }));
                      }}
                    />
                    {/* {featuresList.schedule.appointmentNotifications
                      ?.enabled && ( */}
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={
                        !featuresList.schedule.appointmentNotifications?.enabled
                      }
                      onClick={() => {
                        const currentNotifications = featuresList.schedule
                          ?.appointmentNotifications || {
                          enabled: true,
                          triggers: [],
                        };
                        const newTrigger = {
                          enabled: true,
                          message: 'Your appointment is coming up',
                          minutesBefore: 60,
                          target: '',
                          type: 'reminder',
                        };

                        setFeaturesList((prev) => ({
                          ...prev,
                          schedule: {
                            ...prev.schedule,
                            appointmentNotifications: {
                              ...currentNotifications,
                              triggers: [
                                newTrigger,
                                ...(currentNotifications.triggers || []),
                              ],
                            },
                          },
                        }));
                      }}
                    >
                      Add Notification
                    </Button>
                    {/* )} */}
                  </Box>
                </Box>
                {featuresList.schedule.appointmentNotifications?.enabled && (
                  <Box style={{ maxWidth: '100%' }}>
                    {loadingSmsVariables ? (
                      <Box display="flex" justifyContent="center" padding={3}>
                        <CircularProgress />
                      </Box>
                    ) : smsVariables.length > 0 ? (
                      featuresList.schedule.appointmentNotifications.triggers?.map(
                        (trigger, index) => (
                          <SMSTrigger
                            key={index}
                            index={index}
                            trigger={trigger}
                            setFeaturesList={setFeaturesList}
                            featuresList={featuresList}
                            smsVariables={smsVariables}
                          />
                        )
                      )
                    ) : (
                      <Box padding={2} textAlign="center">
                        <Text>No SMS variables available</Text>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Games
              </Text>
              <Switch
                checked={featuresList.games?.enabled ?? true}
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    games: {
                      ...featuresList.games,
                      enabled: !(featuresList.games?.enabled ?? true),
                    },
                  }))
                }
              />
            </Box>
            {(featuresList.games?.enabled ?? true) && (
              <Box>
                <Box className="flex-centered space-b g-hov">
                  <Text style={{ fontWeight: 'bold' }}>Bracket Creation</Text>
                  <Switch
                    checked={featuresList.games?.bracketCreation ?? true}
                    onChange={() =>
                      setFeaturesList(() => ({
                        ...featuresList,
                        games: {
                          ...featuresList.games,
                          bracketCreation: !(
                            featuresList.games?.bracketCreation ?? true
                          ),
                        },
                      }))
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Dashboard
              </Text>
              <Switch
                checked={!(featuresList.dashboard?.disabled ?? false)}
                onChange={() =>
                  setFeaturesList(() => ({
                    ...featuresList,
                    dashboard: {
                      ...featuresList.dashboard,
                      disabled: !(featuresList.dashboard?.disabled ?? false),
                    },
                  }))
                }
              />
            </Box>
          </Box>
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                {orgTiles.find((el) => el.title === 'Calendar')
                  ? 'Disable Calendar'
                  : 'Enable Calendar'}
              </Text>
              <Switch
                checked={!!orgTiles.find((el) => el.title === 'Calendar')}
                onChange={() => {
                  const newOrgTiles = [...orgTiles];
                  const tile = newOrgTiles.find(
                    (el) => el.title === 'Calendar'
                  );
                  if (tile) {
                    const tileIndex = newOrgTiles.indexOf(tile);
                    newOrgTiles.splice(tileIndex, 1);
                  } else {
                    newOrgTiles.unshift({
                      color: 'black',
                      iconName: 'play',
                      permission: 'everyone',
                      screen: 'CalendarScreen',
                      title: 'Calendar',
                      type: 'font-awesome-5',
                    });
                  }
                  setOrgTiles(newOrgTiles);
                }}
              />
            </Box>
          </Box>
          {/* Recording Section */}
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                Recording
              </Text>
            </Box>
            <Box>
              <Box className="flex-centered space-b g-hov">
                <Text style={{ fontWeight: 'bold' }}>Auto Recording</Text>
                <Switch
                  checked={featuresList?.recording?.autoRecording || false}
                  onChange={() =>
                    setFeaturesList((prev) => ({
                      ...prev,
                      recording: {
                        ...(prev.recording || {}),
                        autoRecording: !(
                          prev.recording?.autoRecording || false
                        ),
                      },
                    }))
                  }
                />
              </Box>
            </Box>
          </Box>
          {/* Estimates Section */}
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                Estimates
              </Text>
            </Box>
            <Box>
              <Box className="flex-centered space-b g-hov">
                <Text style={{ fontWeight: 'bold' }}>Demo Mode</Text>
                <Switch
                  checked={featuresList?.estimates?.demoMode ?? true}
                  onChange={() =>
                    setFeaturesList((prev) => ({
                      ...prev,
                      estimates: {
                        ...prev?.estimates,
                        demoMode: !(prev?.estimates?.demoMode ?? true),
                      },
                    }))
                  }
                />
              </Box>
              <Box
                className="flex-centered space-b g-hov"
                style={{ marginTop: 5 }}
              >
                <Text style={{ fontWeight: 'bold' }}>
                  Price Per Estimate ($)
                </Text>
                <TextField
                  type="number"
                  variant="standard"
                  size="small"
                  value={(
                    (featuresList?.estimates?.pricePerEstimate ?? 9900) / 100
                  ).toFixed(2)}
                  onChange={(e) => {
                    const dollars = parseFloat(e.target.value);
                    const cents = Math.round(dollars * 100);
                    setFeaturesList((prev) => ({
                      ...prev,
                      estimates: {
                        ...prev?.estimates,
                        pricePerEstimate: isNaN(cents) ? '' : cents,
                      },
                    }));
                  }}
                  style={{ width: 100 }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Box>
              <Box
                style={{
                  paddingTop: 10,
                  borderTop: '1px solid #eee',
                  marginTop: 10,
                }}
              >
                <Text style={{ fontWeight: 'bold', marginBottom: 5 }}>
                  Credits
                </Text>
                <Box style={{ display: 'flex', gap: 15, marginBottom: 8 }}>
                  <Text style={{ fontSize: '0.9rem' }}>
                    Granted: {estimateCredits.granted}
                  </Text>
                  <Text style={{ fontSize: '0.9rem' }}>
                    Used: {estimateCredits.used}
                  </Text>
                  <Text style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Remaining: {estimateCredits.remaining}
                  </Text>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TextField
                    type="number"
                    variant="outlined"
                    size="small"
                    placeholder="Credits to add"
                    value={creditsToAdd}
                    onChange={(e) => setCreditsToAdd(e.target.value)}
                    style={{ width: 120 }}
                    inputProps={{ min: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleGrantCredits}
                    disabled={creditsLoading}
                  >
                    {creditsLoading ? 'Adding...' : 'Add Credits'}
                  </Button>
                </Box>
                {creditsError && (
                  <Text
                    style={{ color: 'red', marginTop: 4, fontSize: '0.8rem' }}
                  >
                    {creditsError}
                  </Text>
                )}
                {creditsSuccess && (
                  <Text
                    style={{ color: 'green', marginTop: 4, fontSize: '0.8rem' }}
                  >
                    {creditsSuccess}
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
          {/* Instant Roofer - Manual Inspection Section */}
          <Box className="multi-att-feature-box">
            <Box className="flex-centered space-b">
              <Text style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                Instant Roofer - Manual Inspection
              </Text>
            </Box>
            <Box>
              <Box className="flex-centered space-b g-hov">
                <Text style={{ fontWeight: 'bold' }}>Demo Mode</Text>
                <Switch
                  checked={featuresList?.irHumanReport?.demoMode ?? false}
                  onChange={() =>
                    setFeaturesList((prev) => ({
                      ...prev,
                      irHumanReport: {
                        ...prev?.irHumanReport,
                        demoMode: !(prev?.irHumanReport?.demoMode ?? false),
                      },
                    }))
                  }
                />
              </Box>
              <Box
                className="flex-centered space-b g-hov"
                style={{ marginTop: 5 }}
              >
                <Text style={{ fontWeight: 'bold' }}>Price Per Report ($)</Text>
                <TextField
                  type="number"
                  variant="standard"
                  size="small"
                  value={(
                    (featuresList?.irHumanReport?.pricePerReport ?? 1000) / 100
                  ).toFixed(2)}
                  onChange={(e) => {
                    const dollars = parseFloat(e.target.value);
                    const cents = Math.round(dollars * 100);
                    setFeaturesList((prev) => ({
                      ...prev,
                      irHumanReport: {
                        ...prev?.irHumanReport,
                        pricePerReport: isNaN(cents) ? '' : cents,
                      },
                    }));
                  }}
                  style={{ width: 100 }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Box>
              <Box
                style={{
                  paddingTop: 10,
                  borderTop: '1px solid #eee',
                  marginTop: 10,
                }}
              >
                <Text style={{ fontWeight: 'bold', marginBottom: 5 }}>
                  Credits
                </Text>
                <Box style={{ display: 'flex', gap: 15, marginBottom: 8 }}>
                  <Text style={{ fontSize: '0.9rem' }}>
                    Granted: {irCredits.granted}
                  </Text>
                  <Text style={{ fontSize: '0.9rem' }}>
                    Used: {irCredits.used}
                  </Text>
                  <Text style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Remaining: {irCredits.remaining}
                  </Text>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TextField
                    type="number"
                    variant="outlined"
                    size="small"
                    placeholder="Credits to add"
                    value={irCreditsToAdd}
                    onChange={(e) => setIrCreditsToAdd(e.target.value)}
                    style={{ width: 120 }}
                    inputProps={{ min: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleGrantIrCredits}
                    disabled={irCreditsLoading}
                  >
                    {irCreditsLoading ? 'Adding...' : 'Add Credits'}
                  </Button>
                </Box>
                {irCreditsError && (
                  <Text
                    style={{ color: 'red', marginTop: 4, fontSize: '0.8rem' }}
                  >
                    {irCreditsError}
                  </Text>
                )}
                {irCreditsSuccess && (
                  <Text
                    style={{ color: 'green', marginTop: 4, fontSize: '0.8rem' }}
                  >
                    {irCreditsSuccess}
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
      {/* Permissions */}
      <Box
        style={{
          marginTop: 10,
          backgroundColor: 'white',
          border: '1px solid black',
          borderRadius: '15px',
          padding: 15,
        }}
      >
        <Box className="center-content">
          <Text
            style={{ fontWeight: 'bold', fontSize: '1.8rem', color: '#FF6A00' }}
          >
            PERMISSIONS
          </Text>
        </Box>
        <Box style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {permissionsArray.map((per, i) => (
            <Box key={i} style={{ width: '18%' }}>
              <Text style={{ fontWeight: 600 }}>{per.label}</Text>
              <Select
                value={orgPermissions[per.key] || ''}
                onChange={(e) => {
                  setOrgPermissions({
                    ...orgPermissions,
                    [per.key]: e.target.value,
                  });
                }}
                style={{ width: '100%' }}
              >
                {!orgPermissions[per.key] && (
                  <MenuItem value="" style={{ backgroundColor: 'red' }}>
                    <em>Not selected</em>
                  </MenuItem>
                )}
                {['admin', 'moderator', 'everyone'].map((opt, j) => (
                  <MenuItem
                    style={{
                      backgroundColor: 'white',
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = 'lightgray')
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = 'white')
                    }
                    value={opt}
                    key={j}
                  >
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ))}
        </Box>
      </Box>
      {/* Damage Reports Configuration */}
      <Box
        style={{
          marginTop: 10,
          backgroundColor: 'white',
          border: '1px solid black',
          borderRadius: '15px',
          padding: 15,
        }}
      >
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            style={{ fontWeight: 'bold', fontSize: '1.8rem', color: '#FF6A00' }}
          >
            DAMAGE REPORTS
          </Text>
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <Text style={{ fontWeight: 600, marginRight: 10 }}>Enabled</Text>
            <Switch
              checked={damageReportsEnabled}
              onChange={(e) => {
                setDamageReportsEnabled(e.target.checked);
              }}
            />
          </Box>
        </Box>

        {damageReportsEnabled && (
          <Box style={{ marginTop: 15 }}>
            <Box style={{ marginBottom: 20 }}>
              <Text
                style={{
                  fontWeight: 600,
                  marginBottom: 15,
                  fontSize: '1.1rem',
                }}
              >
                Logo
              </Text>
              <Box
                style={{
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                {damageReportsLogo ? (
                  <Box>
                    <img
                      src={`https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
                        damageReportsLogo
                      )}`}
                      alt="Logo"
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: 'contain',
                        marginBottom: '15px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Box style={{ marginTop: '10px' }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            uploadLogo(file);
                          }
                        }}
                        style={{ display: 'none' }}
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          disabled={uploadingLogo}
                          style={{ marginRight: '10px' }}
                        >
                          {uploadingLogo ? 'Uploading...' : 'Replace Logo'}
                        </Button>
                      </label>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box
                      style={{
                        width: 120,
                        height: 120,
                        border: '2px dashed #ddd',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 15px auto',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <Text style={{ color: '#999', fontSize: '0.9rem' }}>
                        No Logo
                      </Text>
                    </Box>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          uploadLogo(file);
                        }
                      }}
                      style={{ display: 'none' }}
                      id="logo-upload"
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    </label>
                  </Box>
                )}
              </Box>
            </Box>

            <Box>
              <Text
                style={{
                  fontWeight: 600,
                  marginBottom: 15,
                  fontSize: '1.1rem',
                }}
              >
                Hero Images
              </Text>
              <Box
                style={{
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#fafafa',
                  minHeight: '120px',
                }}
              >
                {damageReportsHeroImages.length > 0 ? (
                  <Box
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 15,
                      marginBottom: 15,
                    }}
                  >
                    {damageReportsHeroImages.map((image, index) => (
                      <Box key={index} style={{ position: 'relative' }}>
                        <img
                          src={`https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
                            image
                          )}`}
                          alt={`Hero ${index + 1}`}
                          style={{
                            width: 180,
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: '6px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeHeroImage(index)}
                          style={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: '#ff4444',
                            color: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            '&:hover': {
                              backgroundColor: '#cc0000',
                            },
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#999',
                    }}
                  >
                    <Text style={{ fontSize: '0.9rem', marginBottom: '10px' }}>
                      No hero images uploaded yet
                    </Text>
                  </Box>
                )}
                <Box style={{ textAlign: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        uploadHeroImage(file);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="hero-image-upload"
                  />
                  <label htmlFor="hero-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={uploadingHeroImage}
                      startIcon={<Add />}
                    >
                      {uploadingHeroImage ? 'Uploading...' : 'Add Hero Image'}
                    </Button>
                  </label>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {/* Web Interface */}
      <Box id="web-interface-box">
        <CSSTransition timeout={500} classNames="item">
          <View style={{ paddingTop: 20, display: 'flex', gap: 15 }}>
            <Text style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
              Web Interface
            </Text>
          </View>
        </CSSTransition>
        <View style={{ paddingTop: 10 }}>
          <View
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 0',
            }}
          >
            <Text>Hide Products on Web</Text>
            <Switch
              checked={featuresList?.webInterface?.hideProducts || false}
              onChange={() =>
                setFeaturesList((prev) => ({
                  ...prev,
                  webInterface: {
                    ...prev?.webInterface,
                    hideProducts: !prev?.webInterface?.hideProducts,
                  },
                }))
              }
            />
          </View>
          <View
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 0',
            }}
          >
            <Text>Hide Feed on Web</Text>
            <Switch
              checked={featuresList?.webInterface?.hideFeed || false}
              onChange={() =>
                setFeaturesList((prev) => ({
                  ...prev,
                  webInterface: {
                    ...prev?.webInterface,
                    hideFeed: !prev?.webInterface?.hideFeed,
                  },
                }))
              }
            />
          </View>
          <View
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 0',
            }}
          >
            <Text>Disable Scheduling on Web</Text>
            <Switch
              checked={featuresList?.webInterface?.disableScheduling || false}
              onChange={() =>
                setFeaturesList((prev) => ({
                  ...prev,
                  webInterface: {
                    ...prev?.webInterface,
                    disableScheduling: !prev?.webInterface?.disableScheduling,
                  },
                }))
              }
            />
          </View>
        </View>
      </Box>
      <Snackbar open={success} autoHideDuration={6000}>
        <Alert severity="success" sx={{ width: '100%', padding: '15px 20px' }}>
          Org Successfully Updated!
        </Alert>
      </Snackbar>
      {/* View Changes Modal */}
      <ChangesModal
        open={showChangesModal}
        onClose={() => setShowChangesModal(false)}
        changes={getAllChanges()}
        title="View Changes"
        isConfirmation={false}
      />
      {/* Confirm Save Modal */}
      <ChangesModal
        open={showConfirmSaveModal}
        onClose={() => setShowConfirmSaveModal(false)}
        changes={getAllChanges()}
        title="Confirm Save Changes"
        isConfirmation={true}
        onConfirm={handleConfirmSave}
        loading={isSaving}
      />
      {/* Confirm Reset Modal */}
      <ChangesModal
        open={showConfirmResetModal}
        onClose={() => setShowConfirmResetModal(false)}
        changes={getAllChanges()}
        title="Confirm Reset Changes"
        isConfirmation={true}
        onConfirm={handleConfirmReset}
        loading={false}
      />
    </View>
  );
}
